/**
 * MessageBroadcastingInfoRemapper
 *
 * Bridges the MESSAGE-level broadcasting struct between the data and domain
 * layers, in BOTH directions:
 *  - read: `fromJson` (audience liveness parse) → `fromDto` → entity
 *  - write: entity → `toDtoFromEntity` → `toJson` (broadcaster start-live write)
 *
 * Flutter source: lib/data/models/message_broadcasting_info.dart:6-20 +
 * message_broadcasting_info.g.dart:9-30 (field names, defaults, and the
 * always-emit-every-field `toJson`).
 */
import type { MessageBroadcastingInfoDto } from '@/data/dtos/info/messageBroadcastingInfoDto';
import type { JsonObject } from '@/data/types/json';
import type { MessageBroadcastingInfoEntity } from '@/domain/entities/info/messageBroadcastingInfoEntity';
import { LatestLiveGiftInfoRemapper } from '@/domain/remappers/info/latestLiveGiftInfoRemapper';

export class MessageBroadcastingInfoRemapper {
  /**
   * Parse a raw `broadcastingInfo` map into a DTO, applying the Flutter
   * generated `fromJson` defaults (message_broadcasting_info.g.dart:9-19):
   * `broadcasterStreamingId`/`likes`/`watchers` → 0, `coins` → 0, `isLive`
   * → false, `latestLiveGiftInfo` carried through only when present.
   */
  static fromJson(data: JsonObject): MessageBroadcastingInfoDto {
    const dto: MessageBroadcastingInfoDto = {
      broadcasterStreamingId: (data['broadcasterStreamingId'] as number | undefined) ?? 0,
      isLive: (data['isLive'] as boolean | undefined) ?? false,
      likes: (data['likes'] as number | undefined) ?? 0,
      coins: (data['coins'] as number | undefined) ?? 0,
      watchers: (data['watchers'] as number | undefined) ?? 0,
    };

    const latestLiveGiftInfo = data['latestLiveGiftInfo'] as JsonObject | undefined | null;
    if (latestLiveGiftInfo) {
      dto.latestLiveGiftInfo = latestLiveGiftInfo;
    }

    return dto;
  }

  /**
   * DTO → entity. The scalar fields assign through; `latestLiveGiftInfo` is the
   * one typed hop — the DTO keeps the opaque `JsonObject` for the wire
   * round-trip, so parse it into the typed `LatestLiveGiftInfoEntity` here (via
   * `LatestLiveGiftInfoRemapper.fromJson`, which applies the Flutter defaults).
   */
  static fromDto(dto: MessageBroadcastingInfoDto): MessageBroadcastingInfoEntity {
    return {
      ...dto,
      latestLiveGiftInfo: dto.latestLiveGiftInfo
        ? LatestLiveGiftInfoRemapper.fromJson(dto.latestLiveGiftInfo)
        : undefined,
    };
  }

  /**
   * Serialize a DTO to the wire map. Mirrors Flutter's generated `toJson`
   * (message_broadcasting_info.g.dart:21-30) which always writes every scalar
   * field. This is the exact shape the START-live write persists.
   */
  static toJson(dto: MessageBroadcastingInfoDto): JsonObject {
    const json: JsonObject = {
      broadcasterStreamingId: dto.broadcasterStreamingId,
      isLive: dto.isLive,
      likes: dto.likes,
      coins: dto.coins,
      watchers: dto.watchers,
    };

    if (dto.latestLiveGiftInfo !== undefined) {
      json.latestLiveGiftInfo = dto.latestLiveGiftInfo;
    }

    return json;
  }

  /**
   * Entity → DTO. The scalar fields assign through; `latestLiveGiftInfo` is
   * serialized back to the opaque `JsonObject` the DTO expects (via
   * `LatestLiveGiftInfoRemapper.toJson`) for the wire round-trip.
   */
  static toDtoFromEntity(entity: MessageBroadcastingInfoEntity): MessageBroadcastingInfoDto {
    return {
      ...entity,
      latestLiveGiftInfo: entity.latestLiveGiftInfo
        ? LatestLiveGiftInfoRemapper.toJson(entity.latestLiveGiftInfo)
        : undefined,
    };
  }
}
